

import React, { useState, useEffect } from 'react'

import { useDropzone } from 'react-dropzone'

import { fieldCannotBeEmpty } from 'constants/messages'

import { Typography, FormControl, FormHelperText, Stack, Avatar, useTheme, Box } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { Button } from '@repo/ui'
import { Building2 } from 'lucide-react'

import type { FieldValues } from 'react-hook-form'

import { FieldProps } from './types'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png']

// The "on light background" swatch stands for a FIXED background the logo will be
// placed on -- it is not the app's own surface, so it must not follow the app theme.
// `theme.palette.grey` is remapped per mode (theme.ts darkNeutral: grey[50] becomes
// #18181b in dark mode), which made the swatch labelled "on light background" render
// DARK whenever the admin viewed the page in dark mode. These literals are the
// light-mode values of grey[50] / grey[200] / grey[400].
const LIGHT_SWATCH_BG = 'hsla(180, 5%, 96%, 1)'
const LIGHT_SWATCH_BORDER = 'hsla(216, 5%, 82%, 1)'
const LIGHT_SWATCH_ICON = 'hsla(220, 5%, 53%, 1)'

/**
 * Crops and resizes an image to a 1:1 aspect ratio (square)
 * @param file - The image file to process
 * @param size - The output size (default: 512x512)
 * @returns Promise<File> - The processed image file
 */
const cropImageToSquare = (file: File, size: number = 512): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        // Calculate crop dimensions (center crop)
        const minDimension = Math.min(img.width, img.height)
        const startX = (img.width - minDimension) / 2
        const startY = (img.height - minDimension) / 2

        // Create canvas
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }

        // Draw cropped and resized image
        ctx.drawImage(
          img,
          startX,
          startY,
          minDimension,
          minDimension,
          0,
          0,
          size,
          size
        )

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'))
              return
            }

            // Create a new File from the blob
            const processedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            })
            resolve(processedFile)
          },
          file.type,
          0.95 // Quality (0.95 = 95% quality for JPEG)
        )
      }
      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = e.target?.result as string
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

// Lifted out of the parameter position (it was an inline intersection) so the form type can
// be named and threaded through — see the `FieldProps` docblock in `./types`.
interface AvatarFieldProps<T extends FieldValues = FieldValues> extends FieldProps<T> {
  isCompany?: boolean
  // Render the square tile on a dark swatch (for an on-dark / knockout logo variant).
  dark?: boolean
  value?: { file: File | null; preview_url: string; isUploaded: boolean }
  onChange?: (value: { file: File | null; preview_url: string; isUploaded: boolean }) => void
  error?: string
  // Initials shown as the empty-state placeholder for personal avatars, for
  // parity with the sidebar user avatar (which uses initials, not a silhouette).
  initials?: string
}

const AvatarFieldImpl = ({
  name = 'logo',
  label = '',
  register,
  setValue,
  watch,
  formState,
  defaultValue,
  disabled,
  validation,
  isCompany = false,
  dark = false,
  value,
  onChange,
  error: externalError,
  initials,
}: AvatarFieldProps) => {
  const theme = useTheme()
  // formState is optional — the field can be used controlled (value/onChange)
  // outside a react-hook-form context (e.g. the VRG-18 logo-variant slots).
  const errors = (formState as any)?.errors || {}

  // Use controlled value if provided, otherwise use internal state
  const isControlled = value !== undefined && onChange !== undefined
  const fieldValue = isControlled ? value : (watch ? watch(name) : null)
  const initialPreview = defaultValue || (fieldValue?.preview_url || '')
  const [previewUrl, setPreviewUrl] = useState<string>(initialPreview)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    // Update preview when field value changes
    if (fieldValue?.file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string)
      }
      reader.readAsDataURL(fieldValue.file)
    } else if (fieldValue?.preview_url) {
      setPreviewUrl(fieldValue.preview_url)
    } else if (!fieldValue) {
      setPreviewUrl('')
    }
  }, [fieldValue])

  const processFile = async (file: File) => {
    setError('')

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB')
      return
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Only JPEG, JPG, and PNG files are allowed')
      return
    }

    try {
      // Process image to 1:1 aspect ratio
      const processedFile = await cropImageToSquare(file)

      // Create preview URL from processed file
      const reader = new FileReader()
      reader.onloadend = () => {
        const preview = reader.result as string
        setPreviewUrl(preview)

        const newValue = {
          file: processedFile,
          preview_url: preview,
          isUploaded: false,
        }

        // Update form value with processed file
        if (isControlled && onChange) {
          onChange(newValue)
        } else if (setValue) {
          setValue(name, newValue, { shouldDirty: true })
        }
      }
      reader.readAsDataURL(processedFile)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image')
    }
  }

  const { getRootProps, getInputProps, open, isDragAccept, isDragReject } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE,
    maxFiles: 1,
    multiple: false,
    disabled,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length > 0) {
        processFile(acceptedFiles[0])
      }
      if (rejectedFiles.length > 0) {
        const { errors: fileErrors } = rejectedFiles[0]
        fileErrors.forEach((err) => {
          if (err.code === 'file-too-large') {
            setError('File size must be less than 10MB')
          }
          if (err.code === 'file-invalid-type') {
            setError('Only JPEG, JPG, and PNG files are allowed')
          }
        })
      }
    },
  })

  const handleDelete = () => {
    setPreviewUrl('')
    setError('')
    const newValue = {
      file: null,
      preview_url: '',
      isUploaded: false,
    }
    if (isControlled && onChange) {
      onChange(newValue)
    } else if (setValue) {
      setValue(name, newValue, { shouldDirty: true })
    }
  }

  const displayError = externalError || error || (errors?.[name]?.message as string)

  return (
    <FormControl error={!!displayError} sx={{ width: '100%' }}>
      {label && (
        <Typography mb={3} variant="body3">
          {label}
        </Typography>
      )}
      <Box
        {...getRootProps()}
        sx={{
          borderRadius: 2,
          border: isDragAccept
            ? `2px dashed ${theme.palette.brand[600]}`
            : isDragReject
              ? `2px dashed ${theme.palette.error.main}`
              : '2px dashed transparent',
          backgroundColor: isDragAccept
            ? alpha(theme.palette.brand[600], 0.04)
            : isDragReject
              ? alpha(theme.palette.error.main, 0.04)
              : 'transparent',
          padding: 2,
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        <input {...getInputProps()} />
        <Stack spacing={3} direction="row" alignItems="center">
          {/* No photo: show initials when a name exists, otherwise MUI's default
              Person silhouette (placeholder) — never a pulsing skeleton. */}
          <Avatar
              src={previewUrl}
              variant={isCompany ? 'rounded' : 'circular'}
              alt={initials}
              sx={{
                width: 64,
                height: 64,
                fontSize: 20,
                fontWeight: 600,
                // Company logos render as squared tiles everywhere else (e.g. the
                // Companies table cell, 6px); keep person avatars round.
                borderRadius: isCompany ? '6px' : undefined,
                // Dark swatch for on-dark logo variants (so a white/knockout mark is
                // visible). The light swatch stays a FIXED light tone regardless of the
                // app's theme -- see LIGHT_SWATCH_* above. For a company mark the swatch
                // is kept even once an image is uploaded, because brand marks are usually
                // transparent PNGs and would otherwise show the (dark) card through.
                bgcolor: dark
                  ? '#1b1b1d'
                  : (isCompany
                      ? LIGHT_SWATCH_BG
                      : (!previewUrl ? theme.palette.primary[100] : undefined)),
                color: !previewUrl
                  ? (dark ? alpha('#ffffff', 0.7) : theme.palette.text.secondary)
                  : undefined,
                border: dark
                  ? `1px solid ${alpha('#ffffff', 0.15)}`
                  : (isCompany
                      ? `1px solid ${LIGHT_SWATCH_BORDER}`
                      : (!previewUrl ? `1px solid ${theme.palette.primary[50]}` : undefined)),
              }}
            >
              {!previewUrl && isCompany ? (
                <Building2 strokeWidth={1} size={28} color={dark ? alpha('#ffffff', 0.45) : LIGHT_SWATCH_ICON} />
              ) : (
                // Initials when a name exists; null (not '') so MUI shows its default
                // Person silhouette placeholder when there are none.
                !previewUrl ? (initials || null) : null
              )}
            </Avatar>
          <Button variant="tertiary" onClick={open} disabled={disabled}>
            {previewUrl ? 'Change' : 'Upload'}
          </Button>
          {/* Only show Remove when an avatar actually exists (nothing to remove
              otherwise). Neutral by default, destructive (red) on hover. */}
          {!isCompany && previewUrl && (
            <Button
              variant="text"
              onClick={handleDelete}
              disabled={disabled}
              sx={{
                color: theme.palette.text.secondary,
                transition: 'color 150ms ease, background-color 150ms ease',
                '&:hover': {
                  color: theme.palette.error.main,
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              Remove
            </Button>
          )}
        </Stack>
      </Box>
      {displayError && (
        <FormHelperText>{displayError}</FormHelperText>
      )}
    </FormControl>
  )
}

/**
 * Re-exported under the generic signature so a caller's own form type flows through.
 * See the `FieldProps` docblock in `./types` for why the implementation above stays on the
 * default instantiation and why this assertion is the standard idiom rather than a
 * suppression.
 */
export const AvatarField = AvatarFieldImpl as <T extends FieldValues = FieldValues>(
  props: AvatarFieldProps<T>,
) => React.ReactElement | null
